import { ChangeDetectionStrategy, Component, input, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';

import { SimpleChanges } from '@core-models/utilities/generic-simple-changes';
import { NeighborhoodProperty } from '@on-market/models/neighborhood-property';
import { PatternSearchResult } from '@search/models/pattern-search-result';
import { RpcListItem } from '@core-controls/components/rpc-list-item/models/rpc-list-item';
import { SearchStoreService } from '@search/store/services/search-store.service';
import { OmniboxValue, RpcOmniboxComponent } from '@core-controls/components/rpc-omnibox/rpc-omnibox.component';

type OmniboxValueData = { keys: number[], type: string };

@Component({
    selector: 'omni-search',
    templateUrl: './omni-search.component.html',
    styleUrls: ['./omni-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcOmniboxComponent, AsyncPipe]
})
export class OmniSearchComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public selectedPatternSearchResults: PatternSearchResult[];
    @Input() public disabled?: boolean = false;
    @Input() public readonly?: boolean = false;
    iconName = input<string>('search');

    // eslint-disable-next-line @typescript-eslint/member-ordering
    @ViewChild(RpcOmniboxComponent) omnibox: RpcOmniboxComponent<unknown>;

    public selectedValues: OmniboxValue<OmniboxValueData>[] = [];
    public selectedCurrentPatternSearchResults: PatternSearchResult[] = [];
    public autocomleteOptions$: Observable<RpcListItem<OmniboxValueData>[]>;
    public neighborhoodProperties: NeighborhoodProperty[] = [];

    private readonly unsubscribe$ = new Subject<void>();
    private readonly autocompleteItemIconMap = new Map<string, string>([
        ['borough', 'circle'],
        ['section', 'romb'],
        ['neighborhood', 'gallery'],
        ['street', 'road'],
        ['building name', 'building'],
        ['address', 'address'],
        ['listing', 'listing'],
        ['web', 'web'],
        ['school', 'schools'],
    ]);

    constructor(
        private readonly searchStoreService: SearchStoreService,
    ) { }

    public ngOnChanges(changes: SimpleChanges<OmniSearchComponent>): void {
        if (changes.selectedPatternSearchResults?.currentValue != null) {
            this.selectedCurrentPatternSearchResults = [];
            this.selectedValues = [];

            changes.selectedPatternSearchResults.currentValue.forEach(value => {
                this.selectedCurrentPatternSearchResults.push(value);
                this.selectedValues.push({ title: value.text, text: value.text, value: { keys: value.keys, type: value.type } });
            });
        }
    }

    public ngOnInit(): void {
        this.autocomleteOptions$ = this.searchStoreService.patternSearchResults$.pipe(map(patternSearchResults => {
            const selectedValuesNames = new Set<string>(this.selectedValues.map(value => value.text));

            return this.createAutocompleteList(patternSearchResults, selectedValuesNames);
        }));

        this.searchStoreService.neighborhoodProperties$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(neighborhoodProperties => this.neighborhoodProperties = neighborhoodProperties);
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public onValueAdded(item: OmniboxValue<OmniboxValueData>): void {
        this.selectedValues.push(item);

        const adjustedKeys = ['borough', 'section', 'neighborhood'].includes(item.value.type.toLowerCase())
            ? this.getNeighborhoodChildrenIds(this.neighborhoodProperties, false, item.value.keys[0])
            : item.value.keys;

        this.selectedCurrentPatternSearchResults.push({ ...item.value, text: item.text, keys: adjustedKeys });

        this.searchStoreService.patternResultChanged(this.selectedCurrentPatternSearchResults);
    }

    public onValueRemoved(value: OmniboxValue<OmniboxValueData>): void {
        this.selectedValues = this.selectedValues.filter(x => x.text !== value.text);

        const mathcingPatternIndex = this.selectedCurrentPatternSearchResults.findIndex(patternResult => patternResult.text === value.text);

        if (mathcingPatternIndex >= 0) {
            this.selectedCurrentPatternSearchResults.splice(mathcingPatternIndex, 1);
        }

        this.searchStoreService.patternResultChanged(this.selectedCurrentPatternSearchResults);
    }

    public onPatternChanged(searchPattern: string): void {
        this.searchStoreService.searchByPattern(searchPattern);
    }

    public focus(): void {
        this.omnibox.onFocus();
    }

    private getIcon(searchResultType: string): string {
        return this.autocompleteItemIconMap.get(searchResultType.toLowerCase()) ?? '';
    }

    private getNeighborhoodChildrenIds(neighborhoodProperties: NeighborhoodProperty[], targetIdMatched: boolean, targetId: number = null): number[] {
        const childrenIds: number[] = [];

        for (const property of neighborhoodProperties) {
            if (property.id === targetId) {
                const foundChildrenIds = this.getNeighborhoodChildrenIds(property.children, true);

                childrenIds.push(...foundChildrenIds);

                if (!targetIdMatched && childrenIds.length === 0) {
                    childrenIds.push(property.id);

                    return childrenIds;
                }
            } else {
                if (targetIdMatched) {
                    childrenIds.push(property.id);
                }

                if (property.children.length > 0) {
                    const foundChildrenIds = this.getNeighborhoodChildrenIds(property.children, targetIdMatched, targetId);

                    childrenIds.push(...foundChildrenIds);
                }
            }
        }

        return childrenIds;
    }

    private createAutocompleteList(patternSearchResults: PatternSearchResult[], selectedValuesNames: Set<string>): RpcListItem<OmniboxValueData>[] {
        const autocompleteItemsMap = patternSearchResults.reduce(
            (map, item) => {
                if (selectedValuesNames.has(item.text)) {
                    return map;
                }

                const items: RpcListItem<OmniboxValueData>[] = map.has(item.type)
                    ? [
                        { text: null, value: null, disabled: true, type: 'divider' },
                        { text: item.text, value: { type: item.type, keys: item.keys }, type: 'item', iconName: this.getIcon(item.type) },
                    ]
                    : [
                        { text: null, value: null, disabled: true, type: 'divider' },
                        { text: item.type, value: null, disabled: true, type: 'header' },
                        { text: null, value: null, disabled: true, type: 'divider' },
                        { text: item.text, value: { type: item.type, keys: item.keys }, type: 'item', iconName: this.getIcon(item.type) },
                    ];

                return map.set(item.type, [...(map.get(item.type) ?? []), ...items]);
            },
            new Map<string, RpcListItem<OmniboxValueData>[]>()
        );

        return [...autocompleteItemsMap.values()].flat().slice(1);
    }
}